'use client';

import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useKeyboardOpen } from '@/lib/useKeyboardOpen';

/**
 * Floating "back to top" button, bottom-right. Appears once the user has scrolled
 * past the hero frames section (#hero) — same threshold as the bottom dock — and
 * smooth-scrolls to the top.
 */
export default function BackToTop() {
  const [visible, setVisible] = useState(false);
  // Hide while a soft keyboard is open so it doesn't float over the form field.
  const keyboardOpen = useKeyboardOpen();

  useEffect(() => {
    const hero = document.getElementById('hero');
    const onScroll = () => {
      // Reveal once past the hero scrub range (its height − one viewport), i.e.
      // the point the pinned hero releases and the next section appears. Falls
      // back to ~80% of the first viewport on pages without a hero.
      const threshold = hero ? hero.offsetHeight - window.innerHeight : window.innerHeight * 0.8;
      setVisible(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <button
      type="button"
      onClick={() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Drop any leftover section hash (#instalacje …) so the URL reflects home.
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }}
      aria-label="Back to top"
      className={clsx(
        // On mobile the bottom dock sits centre-bottom and stretches near the
        // right edge, so this button must clear it: lift to ~5rem + the home-
        // indicator safe area + the measured browser-chrome inset (--nav-inset,
        // shared from NavBar) so the two never overlap, even when the iOS toolbar
        // reappears on scroll-up. From sm up the dock narrows away from the corner
        // (and pins to the top at lg), so the original bottom-6 is fine again.
        'fixed bottom-[calc(5rem+env(safe-area-inset-bottom)+var(--nav-inset,0px))] sm:bottom-6 right-margin-mobile z-[80] flex h-11 w-11 items-center justify-center rounded-full border border-outline-variant/40 bg-surface/80 text-on-surface backdrop-blur-xl transition-all duration-300 hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary lg:right-margin-desktop',
        visible && !keyboardOpen
          ? 'translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-3 opacity-0'
      )}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}
